import React from "react"
import hiveOnlineLogo from "../../view/assets/Group 419@2x.png"
import { setCurrentPage } from "../../store/reducers/navigation"
import { logoutAction } from "../../store/actions/auth"

// Fallback translations for when i18n fails
const translations = {
  en: {
    title: "Oops! Something went wrong",
    message: "We're sorry, but an unexpected error occurred. Please try one of the options below to continue.",
    contactSupport: "Contact Support Team",
    backToHome: "Back to Groups",
    refresh: "Refresh Page",
    logout: "Logout",
    offlineNotice: "You are currently offline. Some actions may not be available."
  },
  fr: {
    title: "Oups ! Une erreur s'est produite",
    message: "Nous sommes désolés, mais une erreur inattendue s'est produite. Veuillez essayer une des options ci-dessous pour continuer.",
    contactSupport: "Contacter l'équipe de support",
    backToHome: "Retour aux groupes",
    refresh: "Actualiser la page",
    logout: "Se déconnecter",
    offlineNotice: "Vous êtes actuellement hors ligne. Certaines actions peuvent ne pas être disponibles."
  },
  ar: {
    title: "عذراً! حدث خطأ ما",
    message: "نأسف، لكن حدث خطأ غير متوقع. يرجى تجربة أحد الخيارات أدناه للمتابعة.",
    contactSupport: "التواصل مع فريق الدعم",
    backToHome: "العودة إلى المجموعات",
    refresh: "تحديث الصفحة",
    logout: "تسجيل الخروج",
    offlineNotice: "أنت حالياً غير متصل بالإنترنت. قد لا تكون بعض الإجراءات متاحة."
  },
  es: {
    title: "¡Ups! Algo salió mal",
    message: "Lo sentimos, pero ocurrió un error inesperado. Por favor, intenta una de las opciones de abajo para continuar.",
    contactSupport: "Contactar equipo de soporte",
    backToHome: "Volver a grupos",
    refresh: "Actualizar página",
    logout: "Cerrar sesión",
    offlineNotice: "Actualmente estás desconectado. Algunas acciones pueden no estar disponibles."
  },
  sw: {
    title: "Samahani! Kuna kitu kimekwenda vibaya",
    message: "Tunaomba msamaha, lakini hitilafu isiyotarajiwa imetokea. Tafadhali jaribu moja ya chaguo zilizo hapa chini kuendelea.",
    contactSupport: "Wasiliana na timu ya msaada",
    backToHome: "Rudi kwenye vikundi",
    refresh: "Onyesha upya ukurasa",
    logout: "Toka",
    offlineNotice: "Kwa sasa huna muunganisho wa mtandao. Baadhi ya vitendo vinaweza kutokupatikana."
  },
  pt: {
    title: "Ops! Algo deu errado",
    message: "Desculpe, mas ocorreu um erro inesperado. Por favor, tente uma das opções abaixo para continuar.",
    contactSupport: "Contatar equipe de suporte",
    backToHome: "Voltar aos grupos",
    refresh: "Atualizar página",
    logout: "Sair",
    offlineNotice: "Você está atualmente offline. Algumas ações podem não estar disponíveis."
  },
  lb: {
    title: "عذراً! حدث خطأ ما",
    message: "نأسف، لكن حدث خطأ غير متوقع. يرجى تجربة أحد الخيارات أدناه للمتابعة.",
    contactSupport: "التواصل مع فريق الدعم",
    backToHome: "العودة إلى المجموعات",
    refresh: "تحديث الصفحة",
    logout: "تسجيل الخروج",
    offlineNotice: "أنت حالياً غير متصل بالإنترنت. قد لا تكون بعض الإجراءات متاحة."
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isOnline: navigator.onLine,
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo)
    this.setState({ errorInfo })
  }

  handleNetworkChange = () => {
    this.setState({ isOnline: navigator.onLine })
  }

  componentDidMount() {
    window.addEventListener("online", this.handleNetworkChange)
    window.addEventListener("offline", this.handleNetworkChange)
  }

  componentWillUnmount() {
    window.removeEventListener("online", this.handleNetworkChange)
    window.removeEventListener("offline", this.handleNetworkChange)
  }

  getCurrentPage = () => {
    // Try to get current page from Redux store first
    try {
      if (this.props.store) {
        const state = this.props.store.getState()
        return state.currentPage || window.location.pathname
      }
    } catch (e) {
      // If Redux fails, fall back to window location
    }
    return window.location.pathname || "Unknown"
  }

  getCurrentLanguage = () => {
    // Try to get language from localStorage or fallback to English
    const lang = localStorage.getItem('lang') || 'en'
    return translations[lang] || translations.en
  }

  handleRefresh = () => {
    window.location.reload()
  }

  handleLogout = () => {
    try {
      // Use Redux action for logout if store is available
      if (this.props.store) {
        this.props.store.dispatch(logoutAction())
      } else {
        // Fallback to manual cleanup
        localStorage.clear()
        sessionStorage.clear()
        window.location.href = "/"
      }
    } catch (e) {
      // If Redux fails, fallback to manual cleanup
      localStorage.clear()
      sessionStorage.clear()
      window.location.href = "/"
    }
  }

  handleBackToHome = () => {
    try {
      // Use Redux action to set page to group-main if store is available
      if (this.props.store) {
        this.props.store.dispatch(setCurrentPage("group-main"))
        window.location.href = "/"
      } else {
        // Fallback to direct navigation
        window.location.href = "/"
      }
    } catch (e) {
      // If Redux fails, fallback to direct navigation
      window.location.href = "/"
    }
  }

  render() {
    const { error, isOnline } = this.state

    if (this.state.hasError) {
      const currentPage = this.getCurrentPage()
      const t = this.getCurrentLanguage()

      const subject = encodeURIComponent("App Error - VSLA Online")
      const body = encodeURIComponent(
        `Page: ${currentPage}\n` +
          `Error: ${error?.name || "N/A"} - ${error?.message || "N/A"}\n\n` +
          `Stack Trace:\n${error?.stack || "No stack trace available."}`
      )
      const mailtoLink = `mailto:support@hivenetwork.online?subject=${subject}&body=${body}`

      const overlayStyle = {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "#fff5f5",
        zIndex: 999999,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: "Fira Sans",
        padding: "20px",
      }

      const containerStyle = {
        textAlign: "center",
        padding: "40px",
        maxWidth: "500px",
        width: "100%",
        backgroundColor: "#ffffff",
        border: "1px solid #ffcdd2",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }

      const logoStyle = {
        height: "60px",
        width: "auto",
        maxWidth: "200px",
        marginBottom: "30px",
        objectFit: "contain",
      }

      const iconStyle = {
        fontSize: "48px",
        color: "#d32f2f",
        marginBottom: "20px",
      }

      const titleStyle = {
        color: "#d32f2f",
        fontFamily: "quicksand",
        fontWeight: "bold",
        fontSize: "24px",
        marginBottom: "16px",
      }

      const messageStyle = {
        fontFamily: "Fira Sans",
        fontSize: "16px",
        color: "#666",
        lineHeight: "1.6",
        marginBottom: "20px",
      }

      const errorDetailsStyle = {
        backgroundColor: "#fcfcfc",
        padding: "16px",
        borderRadius: "8px",
        fontSize: "13px",
        fontFamily: "monospace",
        color: "#212121",
        textAlign: "left",
        maxHeight: "150px",
        overflowY: "auto",
        marginBottom: "24px",
        border: "1px solid #e0e0e0",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }

      const actionsStyle = {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginTop: "32px",
        width: "100%",
      }

      const buttonStyle = {
        fontFamily: "Fira Sans",
        fontWeight: "600",
        padding: "14px 28px",
        borderRadius: "8px",
        border: "none",
        cursor: "pointer",
        fontSize: "16px",
        textDecoration: "none",
        display: "inline-block",
        transition: "all 0.3s ease",
        width: "100%",
        textAlign: "center",
        minHeight: "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }

      const primaryButtonStyle = {
        ...buttonStyle,
        backgroundColor: "#E36F1E",
        color: "white",
        boxShadow: "0 2px 8px rgba(227, 111, 30, 0.2)",
      }

      const secondaryButtonStyle = {
        ...buttonStyle,
        backgroundColor: "#f8f9fa",
        color: "#495057",
        border: "2px solid #dee2e6",
      }

      const linkStyle = {
        fontSize: "14px",
        color: "#d32f2f",
        fontWeight: "500",
        marginTop: "16px",
        display: "block",
        textDecoration: "underline",
      }

      return (
        <div style={overlayStyle}>
          <div style={containerStyle}>
            <img src={hiveOnlineLogo} style={logoStyle} alt="HiveOnline" />

            <div style={iconStyle}>⚠️</div>

            <h2 style={titleStyle}>
              {t.title}
            </h2>

            <p style={messageStyle}>
              {t.message}
            </p>

            {error?.message && (
              <div style={errorDetailsStyle}>
                <strong>Error:</strong> {error.name || "Unknown Error"}
                <br />
                <strong>Message:</strong> {error.message}
                <br />
                <strong>Page:</strong> {currentPage}
              </div>
            )}

            <a href={mailtoLink} style={linkStyle}>
              {t.contactSupport}
            </a>

            <div style={actionsStyle}>
              <button
                onClick={this.handleBackToHome}
                style={primaryButtonStyle}
                onMouseOver={(e) => (e.target.style.opacity = "0.9")}
                onMouseOut={(e) => (e.target.style.opacity = "1")}
              >
                {t.backToHome}
              </button>

              <button
                onClick={this.handleRefresh}
                style={secondaryButtonStyle}
                disabled={!isOnline}
                onMouseOver={(e) => !e.target.disabled && (e.target.style.backgroundColor = "#e9ecef")}
                onMouseOut={(e) => !e.target.disabled && (e.target.style.backgroundColor = "#f8f9fa")}
              >
                {t.refresh}
              </button>

              <button
                onClick={this.handleLogout}
                style={secondaryButtonStyle}
                disabled={!isOnline}
                onMouseOver={(e) => !e.target.disabled && (e.target.style.backgroundColor = "#e9ecef")}
                onMouseOut={(e) => !e.target.disabled && (e.target.style.backgroundColor = "#f8f9fa")}
              >
                {t.logout}
              </button>
            </div>

            {!isOnline && (
              <p style={{ ...messageStyle, color: "#d32f2f", marginTop: "20px" }}>
                {t.offlineNotice}
              </p>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary